import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, DatePicker, InputNumber, Select } from 'antd';
import dayjs from '../../../lib/dayjs';
// V1 coupon/list 응답의 periodType은 숫자 코드 — 레거시 정본 상수 재사용 (V2 문자열 enum 아님)
import { COUPON_PERIOD_TYPE } from '../../../constants/service/app/couponConstants';
import { formatCouponPeriod } from '../../../hooks/alimtalk/alimtalkSendDto';
import { AlimtalkSendFormValues } from '../../../types/alimtalk/form';
import { CouponPickerModal } from './CouponPickerModal';
import { CouponRow, useCouponRows } from './useCouponRows';
import * as Styled from './style';

export type CouponIssueSectionProps = {
  needsAlimtalkCouponId: boolean;
  needsValidityDates: boolean;
};

/** 알림톡 표시 쿠폰의 자체 유효기간 안내 — '날짜 지정' 쿠폰은 검증(초 단위 정확 일치)의 기대값이기도 하다 */
const formatCouponPeriodHint = (coupon: CouponRow): string => {
  if (coupon.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
    // 검증 에러 메시지와 동일 포맷 공유 — 힌트와 거절 사유가 어긋나지 않게
    return formatCouponPeriod(coupon.startDate, coupon.endDate);
  }
  if (coupon.periodType === COUPON_PERIOD_TYPE.DAY_PLUS) {
    return `발급일로부터 ${coupon.dayPlus ?? '?'}일`;
  }
  return '무기한';
};

export const CouponIssueSection = (props: CouponIssueSectionProps) => {
  const { needsAlimtalkCouponId, needsValidityDates } = props;
  const { watch, setValue } = useFormContext<AlimtalkSendFormValues>();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const alimtalkCouponId = watch('alimtalkCouponId');
  const couponStartDate = watch('couponStartDate');
  const couponEndDate = watch('couponEndDate');

  const { rows, addCoupons, removeCoupon, setIssuedCount } = useCouponRows({
    defaultIssuedCount: 1,
    // 선택한 쿠폰이 목록에서 빠지면 대표 쿠폰(alimtalkCouponId/Name) 해제
    onRowsChange: (next) => {
      if (alimtalkCouponId && !next.some((row) => row.couponId === alimtalkCouponId)) {
        setValue('alimtalkCouponId', null);
        setValue('previewCouponName', null);
      }
    },
  });

  const selectedAlimtalkCoupon = alimtalkCouponId
    ? rows.find((row) => row.couponId === alimtalkCouponId)
    : undefined;

  // 유효기간 힌트/원클릭의 기준 쿠폰 — 알림톡 표시 쿠폰이 없는 유형(ISSUE_COUPON_ONLY)에서도
  // 검증(validateCouponPeriods)의 기대값인 첫 '날짜 지정' 쿠폰을 보여줘야 입력이 가능하다
  const periodHintCoupon =
    selectedAlimtalkCoupon ?? rows.find((row) => row.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE);

  // 제출용(DTO)·미리보기용을 한 곳에서 기록 — RangePicker 입력과 자동 추종이 공유.
  // ScheduleSection과 동일하게 오프셋 포함 ISO(.format())로 전송 — UTC 'Z' 변환의 9시간 혼동 방지
  const applyValidityDates = (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null): void => {
    const startValue = start ? start.format() : null;
    const endValue = end ? end.format() : null;
    setValue('couponStartDate', startValue);
    setValue('couponEndDate', endValue);
    setValue('previewStartDate', startValue);
    setValue('previewEndDate', endValue);
  };

  // '날짜 지정' 쿠폰이 기준이고 시작·종료가 모두 있으면, 발급 기간은 쿠폰 기간을 그대로 따른다.
  // (정확 일치 정책상 유효한 입력값이 쿠폰 기간 하나뿐이라 수기 입력이 불필요 — 발급일기준·무기한·종료일만 쿠폰은 자동화 불가라 수기 유지)
  const autoFollowCoupon =
    periodHintCoupon?.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE &&
    periodHintCoupon.startDate &&
    periodHintCoupon.endDate
      ? periodHintCoupon
      : undefined;

  // 자동 추종이 "직접 써넣은" 날짜 쌍 — 추종 해제 시 이 값이 그대로 남아 있을 때만 되돌린다.
  // (쿠폰 id가 아니라 기록한 값을 보관해야, 운영자가 같은 기간을 수기로 넣어둔 경우를 자동 입력으로 오인하지 않는다)
  const autoFilledDatesRef = useRef<{ start: string; end: string } | null>(null);

  useEffect(() => {
    if (autoFollowCoupon) {
      const startValue = dayjs(autoFollowCoupon.startDate as string).format();
      const endValue = dayjs(autoFollowCoupon.endDate as string).format();
      // 이미 동일하면 쓸 것도 없다 — 기록도 남기지 않아 기존 입력값의 소유권을 뺏지 않는다
      if (couponStartDate === startValue && couponEndDate === endValue) return;
      autoFilledDatesRef.current = { start: startValue, end: endValue };
      applyValidityDates(dayjs(startValue), dayjs(endValue));
      return;
    }
    // 추종 해제(기준 쿠폰 제거·다른 유형으로 교체) — 자동으로 채웠던 값은 비운다.
    // 남겨두면 무관한 이전 쿠폰의 기간이 그대로 DTO·미리보기에 실려 운영자가 인지 못 한 채 발급된다.
    const autoFilled = autoFilledDatesRef.current;
    if (autoFilled && couponStartDate === autoFilled.start && couponEndDate === autoFilled.end) {
      autoFilledDatesRef.current = null;
      applyValidityDates(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFollowCoupon?.couponId, autoFollowCoupon?.startDate, autoFollowCoupon?.endDate]);

  return (
    <Styled.Section>
      <Styled.SectionTitle>발급 쿠폰<Styled.RequiredMark>*</Styled.RequiredMark></Styled.SectionTitle>

      <Styled.SelectedList>
        {rows.length === 0 && <Styled.EmptyText>선택된 쿠폰이 없습니다.</Styled.EmptyText>}
        {rows.map((row) => (
          <Styled.SelectedRow key={row.couponId}>
            <Styled.SelectedName>
              {row.name} <Styled.IdBadge>#{row.couponId}</Styled.IdBadge>
            </Styled.SelectedName>
            <InputNumber
              min={1}
              value={row.issuedCount}
              onChange={(value) => setIssuedCount(row.couponId, typeof value === 'number' ? value : 1)}
              addonAfter="장"
              style={{ width: 130 }}
            />
            <Button danger size="small" onClick={() => removeCoupon(row.couponId)}>
              삭제
            </Button>
          </Styled.SelectedRow>
        ))}
      </Styled.SelectedList>

      <Button onClick={() => setIsPickerOpen(true)}>＋ 쿠폰 추가</Button>

      {needsAlimtalkCouponId && (
        <Styled.FieldRow>
          <Styled.FieldLabel>알림톡 표시 쿠폰<Styled.RequiredMark>*</Styled.RequiredMark></Styled.FieldLabel>
          <Select
            style={{ width: 320 }}
            placeholder="발급 쿠폰 중 선택"
            value={alimtalkCouponId ?? undefined}
            onChange={(value: number) => {
              setValue('alimtalkCouponId', value);
              setValue('previewCouponName', rows.find((row) => row.couponId === value)?.name ?? null);
            }}
            options={rows.map((row) => ({ value: row.couponId, label: `${row.name} (#${row.couponId})` }))}
          />
        </Styled.FieldRow>
      )}

      {needsValidityDates && (
        <Styled.FieldRow>
          <Styled.FieldLabel>쿠폰 유효기간<Styled.RequiredMark>*</Styled.RequiredMark></Styled.FieldLabel>
          {autoFollowCoupon ? (
            // 쿠폰 기간을 그대로 따르므로 수기 입력 없이 읽기 전용으로 노출 (값은 위 effect가 폼에 기록)
            <Styled.Hint>
              쿠폰({autoFollowCoupon.name})의 유효기간을 따릅니다: {formatCouponPeriodHint(autoFollowCoupon)}
            </Styled.Hint>
          ) : (
            <>
              <DatePicker.RangePicker
                showTime
                style={{ width: 360 }}
                value={[
                  couponStartDate ? dayjs(couponStartDate) : null,
                  couponEndDate ? dayjs(couponEndDate) : null,
                ]}
                onChange={(range) => applyValidityDates(range?.[0] ?? null, range?.[1] ?? null)}
              />
              {periodHintCoupon && (
                <Styled.Hint>
                  쿠폰({periodHintCoupon.name})의 유효기간: {formatCouponPeriodHint(periodHintCoupon)}
                  {/* 종료일만 있는 '날짜 지정' 쿠폰은 종료일만 검증 대상 — 종료일 원클릭 입력 제공 */}
                  {periodHintCoupon.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE &&
                    periodHintCoupon.endDate && (
                      <Button
                        size="small"
                        type="link"
                        onClick={() =>
                          applyValidityDates(
                            periodHintCoupon.startDate
                              ? dayjs(periodHintCoupon.startDate)
                              : couponStartDate
                                ? dayjs(couponStartDate)
                                : null,
                            dayjs(periodHintCoupon.endDate),
                          )
                        }
                      >
                        이 기간으로 입력
                      </Button>
                    )}
                </Styled.Hint>
              )}
            </>
          )}
        </Styled.FieldRow>
      )}

      <CouponPickerModal open={isPickerOpen} onClose={() => setIsPickerOpen(false)} onConfirm={addCoupons} />
    </Styled.Section>
  );
};
